import axios, { AxiosProgressEvent } from 'axios';
import { initAxios } from 'utils/axiosHelper';
import KeycloakService from 'services/KeycloakService';

// Customs Entry Analyzer is served by PyAPI (FastAPI) under /pyapi/customs/*.
// initAxios attaches the Keycloak bearer token that PyAPI requires.
// eslint-disable-next-line no-undef
const PYAPI_BASE_URL = process.env.REACT_APP_PYAPI_URL || '/pyapi';
const apiClient = initAxios(PYAPI_BASE_URL);
apiClient.defaults.timeout = 0;

export interface CustomsAnalyzerResponse {
  [key: string]: any;
}

export interface CustomsUploadProgress {
  loadedBytes: number;
  totalBytes: number;
  percent: number;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : fallback;
  }

  const status = error.response?.status;
  const data = error.response?.data as any;
  const upstreamMessage =
    (typeof data?.message === 'string' && data.message) ||
    (typeof data?.error === 'string' && data.error) ||
    (typeof data?.detail === 'string' && data.detail) ||
    '';

  if (upstreamMessage) {
    return upstreamMessage;
  }
  if (status === 401) {
    return 'Analyzer authentication failed. Please contact support.';
  }
  return fallback;
};

export class CustomsAnalyzerAPI {
  static async createDocumentBatch(
    file: File,
    userId: string,
    additionalColumns?: string,
    onUploadProgress?: (progress: CustomsUploadProgress) => void,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      if (additionalColumns?.trim()) {
        formData.append('additional_columns', additionalColumns.trim());
      }
      formData.append('file', file);

      const response = await apiClient.post(
        '/customs/document-batches',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (!onUploadProgress) {
              return;
            }

            const totalBytes = progressEvent.total || file.size;
            const loadedBytes = Math.min(progressEvent.loaded || 0, totalBytes || 0);
            const percent = totalBytes
              ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100))
              : 0;

            onUploadProgress({
              loadedBytes: loadedBytes,
              totalBytes: totalBytes,
              percent: percent,
            });
          },
        },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'Failed to upload customs documents.'),
      );
    }
  }

  static async runPackageCheck(
    documentBatchId: string,
    userId: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const response = await apiClient.post(
        `/customs/document-batches/${documentBatchId}/package-check`,
        undefined,
        { params: { user_id: userId } },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to run package checker.'));
    }
  }

  static async runEntryParsing(
    documentBatchId: string,
    userId: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const response = await apiClient.post(
        `/customs/document-batches/${documentBatchId}/ingest`,
        undefined,
        { params: { user_id: userId } },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to run entry parsing.'));
    }
  }

  static async runFullComparison(
    documentBatchId: string,
    userId: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const response = await apiClient.post(
        `/customs/document-batches/${documentBatchId}/analyze`,
        undefined,
        { params: { user_id: userId } },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to run full comparison.'));
    }
  }

  static async diffEs003(
    documentBatchId: string,
    file: File,
    userId: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('file', file);

      const response = await apiClient.post(
        `/customs/document-batches/${documentBatchId}/es003-diff`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    } catch (error) {
      throw new Error(
        getErrorMessage(error, 'Failed to compare the ACE ES-003 export.'),
      );
    }
  }

  static async getDocumentBatch(
    documentBatchId: string,
    userId: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const response = await apiClient.get(
        `/customs/document-batches/${documentBatchId}`,
        { params: { user_id: userId } },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load batch status.'));
    }
  }

  // ingest/package-check/analyze kick off a background job and return
  // immediately (see pyapi's start_* wrappers) instead of blocking the
  // request for the minutes OCR-heavy PDFs can take. Poll the batch record
  // for the same result shape the endpoint used to return directly.
  static async pollDocumentBatch(
    documentBatchId: string,
    userId: string,
    intervalMs = 1500,
    timeoutMs = 20 * 60 * 1000,
    // Called with the batch's current `stage` (see analyzer_service.py's
    // _update_stage) whenever it changes, e.g. "Extracted 2 of 5
    // document(s)…" -- lets the caller narrate real backend progress
    // instead of showing one static "Running…" line for the whole job.
    onStage?: (stage: string) => void,
  ): Promise<CustomsAnalyzerResponse> {
    const startedAt = Date.now();
    let lastStage: string | undefined;
    for (;;) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      const record = await CustomsAnalyzerAPI.getDocumentBatch(documentBatchId, userId);
      if (onStage && record.stage && record.stage !== lastStage) {
        lastStage = record.stage;
        onStage(record.stage);
      }
      if (record.status === 'failed') {
        const failedResult = record.last_result || record;
        const message = failedResult.errors?.[0]?.error || 'Analysis failed. Please try again.';
        throw new Error(message);
      }
      if (record.status === 'complete') {
        return record.last_result || record;
      }
      if (Date.now() - startedAt > timeoutMs) {
        throw new Error('Analysis is taking longer than expected. Please check back shortly.');
      }
    }
  }

  static async listArtifacts(
    documentBatchId: string,
    userId: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const response = await apiClient.get(
        `/customs/document-batches/${documentBatchId}/artifacts`,
        { params: { user_id: userId } },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load artifacts.'));
    }
  }

  static async askAgent(
    documentBatchId: string,
    question: string,
    userId: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const response = await apiClient.post('/customs/agent', {
        document_batch_id: documentBatchId,
        question: question,
        user_id: userId,
      });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to query the analyzer agent.'));
    }
  }

  static async saveBatch(
    documentBatchId: string,
    userId: string,
    saved: boolean,
    title?: string,
  ): Promise<CustomsAnalyzerResponse> {
    try {
      const response = await apiClient.post(
        `/customs/document-batches/${documentBatchId}/save`,
        { saved: saved, title: title || null, user_id: userId },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to save the analysis.'));
    }
  }

  static async listSavedBatches(userId: string): Promise<CustomsAnalyzerResponse[]> {
    try {
      const response = await apiClient.get('/customs/document-batches', {
        params: { user_id: userId },
      });
      return response.data?.batches || [];
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load saved analyses.'));
    }
  }

  /**
   * Opens the streaming NDJSON agent endpoint and returns the raw fetch
   * Response so the caller can consume the body as a ReadableStream --
   * same pattern as ReportsAPI.createSession (raw fetch, Keycloak token
   * attached manually since fetch bypasses the axios interceptor in
   * axiosHelper.ts), adapted for newline-delimited JSON instead of SSE.
   */
  static async streamAgent(
    documentBatchId: string,
    question: string,
    userId: string,
    signal?: AbortSignal,
  ): Promise<Response> {
    if (!KeycloakService.isLoggedIn()) {
      KeycloakService.doLogout();
      throw new Error('User is not logged in');
    }
    try {
      await KeycloakService.updateToken();
    } catch (error) {
      if (KeycloakService.isSessionDeadError(error)) {
        KeycloakService.doLogout();
      }
      // Streaming calls bypass axiosHelper. Keep the same rule as the shared
      // interceptor: a transient refresh failure may fail this request, but it
      // must not destroy the user's application session.
      throw error;
    }

    const token = KeycloakService.getToken();
    const response = await fetch(`${PYAPI_BASE_URL}/customs/agent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ document_batch_id: documentBatchId, question: question, user_id: userId }),
      signal: signal,
    });
    if (!response.ok || !response.body) {
      let detail = '';
      try {
        const data = await response.json();
        detail = data?.detail || data?.message || data?.error || '';
      } catch {
        // response body wasn't JSON (or was already consumed) -- fall back below.
      }
      throw new Error(detail || 'Failed to query the analyzer agent.');
    }
    return response;
  }

  static async downloadArtifact(
    documentBatchId: string,
    artifactPath: string,
    userId: string,
  ): Promise<Blob> {
    try {
      const encodedPath = artifactPath
        .split('/')
        .map((part) => encodeURIComponent(part))
        .join('/');

      const response = await apiClient.get(
        `/customs/document-batches/${documentBatchId}/artifacts/${encodedPath}`,
        { responseType: 'blob', params: { user_id: userId } },
      );
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to download artifact.'));
    }
  }
}

export default CustomsAnalyzerAPI;
